import { Inject, Injectable, NgZone } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import {
  ApolloClientOptions,
  ApolloLink,
  ApolloQueryResult,
  FetchResult,
  gql,
  InMemoryCache,
  MutationOptions,
  QueryOptions,
  split,
  SubscriptionOptions,
} from '@apollo/client/core';
import { EmptyObject, ExtraSubscriptionOptions, Flags, MutationResult, NamedOptions } from 'apollo-angular/types';
import { BehaviorSubject, combineLatest, defer, iif, Observable, throwError } from 'rxjs';
import { Globals } from './globals';
import { ConfigService } from './config.service';
import { dematerialize, filter, materialize, switchMap, take, takeWhile, tap } from 'rxjs/operators';
import { getMainDefinition, Observable as ZenObservable } from '@apollo/client/utilities';
import { onError } from '@apollo/client/link/error';
import { getLoadedResults, ignoreFalsy, isEmpty, LoadableHelper, LoadableStatus, Logger, WhereOperator } from '@aiwyn/shared';
import { GraphQLError } from 'graphql';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { AiwynJWT, AuthProviderService, AuthProviderToken, BaseUser } from '@aiwyn/auth';
import { LaunchDarklyFlag } from '../state/launch-darkly/launch-darkly.models';
import { LaunchDarklyService } from './launch-darkly.service';

interface GraphqlEntityReplacement {
  name: string;
  newName: string,
  replaceInResult: boolean;
}

function fromZenObservable<T>(source: Observable<T>): ZenObservable<T> {
  return new ZenObservable(source.subscribe.bind(source));
}

// [Ignored tokens]+ delimit regular tokens
const GRAPHQL_IGNORED_TOKENS: string = ',\\s\uFEFF';
// Punctuation specifies different sections of the query
const GRAPHQL_PUNCTUATION: string = '\\!\\$\\(\\)\\{\\}\\[\\]\\:\\=\\@\\|';
const GRAPHQL_MUTATION_COMMANDS: string[] = ['insert', 'delete', 'update'];
const GRAPHQL_WHERE_OPERATORS: string = Object.values(WhereOperator).join('|');
const GRAPHQL_OPERATIONS: string[] = ['query', 'mutation', 'subscription'];
const OFF_SWITCH_PAIRS: Record<string, string> = {
  '(': ')',
  '"': '"',
};
const CLIENT_PORTAL_ALLOWED_ROLE_HEADER = 'x-hasura-role';

@Injectable({
  providedIn: 'root'
})
export class ExtendedApollo extends Apollo {

  private readonly _didInit$: BehaviorSubject<LoadableStatus> = new BehaviorSubject<LoadableStatus>(LoadableStatus.DEFAULT);

  constructor(
    // VVV New Dependencies VVV
    protected configService: ConfigService,
    protected globals: Globals,
    protected httpLink: HttpLink,
    // Similar to the AuthInterceptor, this is only available for lower level services
    @Inject(AuthProviderToken) private readonly authProviderService: AuthProviderService,
    protected logger: Logger,
    protected launchDarklyService: LaunchDarklyService,
    // VVV Apollo Dependencies VVV
    _ngZone: NgZone,
    apolloOptions?: ApolloClientOptions<any>,
    apolloNamedOptions?: NamedOptions,
    flags?: Flags
  ) {
    super(_ngZone, apolloOptions, apolloNamedOptions, flags);
  }

  /**
   *
   * ExtendedApollo is here to make sure that the Apollo is correctly initialized before being used. Apollo WILL NOT WORK if one of the
   * three following functions isn't used.
   *
   */

  query<T, V = EmptyObject>(options: QueryOptions<V, T>): Observable<ApolloQueryResult<T>> {
    return iif (
      () => this._didInit$.value === LoadableStatus.LOADED,
      defer(() => super.query(options)),
      defer(() => this.initApollo().pipe(
        switchMap(_ => super.query(options).pipe(
          materialize()
        )),
        takeWhile(notification => notification.kind !== 'C'),
        dematerialize(),
      ))
    );
  }
  mutate<T, V = EmptyObject>(options: MutationOptions<T, V>): Observable<MutationResult<T>> {
    return iif (
      () => this._didInit$.value === LoadableStatus.LOADED,
      defer(() => super.mutate(options)),
      defer(() => this.initApollo().pipe(
        switchMap(_ => super.mutate(options).pipe(
          materialize()
        )),
        takeWhile(notification => notification.kind !== 'C'),
        dematerialize(),
      ))
    );
  }
  subscribe<T, V = EmptyObject>(options: SubscriptionOptions<V, T>, extra?: ExtraSubscriptionOptions): Observable<FetchResult<T>> {
    return iif (
      () => this._didInit$.value === LoadableStatus.LOADED,
      defer(() => super.subscribe(options)),
      defer(() => this.initApollo().pipe(
        switchMap(_ => super.subscribe(options).pipe(
          materialize()
        )),
        takeWhile(notification => notification.kind !== 'C'),
        dematerialize(),
      ))
    );
  }

  /**
   * Waits for the config service to load configs before allowing apollo to initialize
   */
  initApollo(): Observable<unknown> {
    return iif(
      () => LoadableHelper.shouldRequest(this._didInit$.value),
      defer(() => {
        this._didInit$.next(LoadableStatus.LOADING);

        return combineLatest([
          this.configService.configValuesLoaded$.pipe(ignoreFalsy()),
          getLoadedResults(this.authProviderService.getUserLoadable()).pipe(
            ignoreFalsy(),
            take(1),
          ),
          this.launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_CLIENT_PORTAL_HASURA_ROLES)
        ]).pipe(
          tap(([didLoadConfigs, baseUser, enableClientPortalHasuraRoles]) => {
            super.create(this.constructOptions(baseUser, enableClientPortalHasuraRoles));
            this._didInit$.next(LoadableStatus.LOADED);
          })
        );
      }),
      defer(() => this._didInit$.pipe(
        filter(status => status === LoadableStatus.LOADED)
      ))
    );
  }

  /**
   * Previously the Apollo provider, used to generate the correct configurations for the HttpLink
   */
  constructOptions(baseUser: BaseUser<AiwynJWT>, enableClientPortalHasuraRoles: boolean): ApolloClientOptions<any> {
    // Create static query link
    const http = this.httpLink.create({
      uri: `http${this.globals.disableSSL ? '' : 's'}://${this.globals.hasuraUrl}/v1/graphql`
    });

    // Create Web Socket
    const wsLink = new GraphQLWsLink(
      createClient({
        url: `wss://${this.globals.hasuraUrl}/v1/graphql`,
        connectionParams: async () => {
          return {
            headers: {
              'Authorization': `Bearer ${baseUser.getToken()}`,
            },
          };
        },
      }),
    );

    // Split link based on subscription operations to use the web socket
    const splitLink = split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return (
          definition.kind === 'OperationDefinition' &&
          definition.operation === 'subscription'
        );
      },
      wsLink,
      http,
    );

    /**
     * Handles errors thrown by the Apollo client.
     * If the error is auth related, it will attempt to get a new JWT token and retry the request.
     */
    const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
      if (graphQLErrors?.find(error => ['invalid-jwt', 'jwt-invalid-claims'].includes(error.extensions.code))) {
        // TODO :: validate
        return fromZenObservable(this.authProviderService.refreshToken()).flatMap(() => forward(operation));
      }

      if (!isEmpty(graphQLErrors)) {
        const errorMessages = graphQLErrors.map(err => err.message).join(', ');
        this.logger.error(errorMessages);
        return fromZenObservable(throwError(new GraphQLError(errorMessages)));
      }

      if (networkError) {
        this.logger.error(networkError.message);
        return fromZenObservable(throwError(new Error('There was a network error. Please check your connection.')));
      }
    });

    // Add the `x-hasura-role` header for the client portal specific permissions.
    const clientPortalAllowedRolesMiddleware = new ApolloLink((operation, forward) => {
      operation.setContext(({ headers = {} }) => ({
        headers: {
          ...headers,
          [CLIENT_PORTAL_ALLOWED_ROLE_HEADER]: baseUser?.getClientPortalAllowedRole() || undefined,
        }
      }));

      return forward(operation);
    });

    // Make a link to update the Apollo requests with the tenant name, and remove the tenant name from the result set
    const tenantQueryAdaptorMiddleware = new ApolloLink((operation, forward) => {
      // Grab the entity names that need to be updated with the tenant
      const entityNames: GraphqlEntityReplacement[] = ExtendedApollo.getGqlEntityNames(
        operation.query.definitions.filter(({ kind }) => kind !== 'FragmentDefinition')
          .reduce((totalSelections, currentDefinition) => [...totalSelections, ...currentDefinition['selectionSet'].selections], []),
        operation.query.loc.source.body,
        this.globals.anTenant
      );

      const fragmentNames: GraphqlEntityReplacement[] = ExtendedApollo.getGqlFragmentNames(
        operation.query.definitions.filter(({ kind }) => kind === 'FragmentDefinition').map(definition => definition['typeCondition']),
        this.globals.anTenant,
      );

      let queryString = ExtendedApollo.updateQueryWithTenant(operation.query.loc.source.body, entityNames);
      queryString = ExtendedApollo.updateFragmentsWithTenant(queryString, fragmentNames);

      // Update the query with the tenant
      operation.query = gql`${queryString}`;
      // Rename the updated entity name with the tenant from the result set unless it was aliased
      return forward(operation).map(response => {
        //If there are errors in the response body, return the errors
        if (response['errors']) {
          return response;
        }

        // For each entity name that was replaced, find it in the results and remove the tenant
        return entityNames.reduce(
          (_, entityReplacement) => {
            // Skip re-name if it was aliased
            if (!entityReplacement.replaceInResult) {
              return response;
            }

            // Add field with old name to result set, remove updated name;
            if (response.data[entityReplacement.newName]) {
              response.data[entityReplacement.name] = response.data[entityReplacement.newName];
              delete response.data[entityReplacement.newName];
            }

            return response;
          },
          response
        );
      });
    });

    let link = tenantQueryAdaptorMiddleware;
    if (enableClientPortalHasuraRoles) {
      this.logger.info('ExtendedApollo :: constructOptions :: Adding client portal allowed roles middleware');
      link = link.concat(clientPortalAllowedRolesMiddleware);
    }
    link = link.concat(errorLink).concat(splitLink)

    return {
      link,
      cache: new InMemoryCache(),
      defaultOptions: {
        watchQuery: {
          fetchPolicy: 'no-cache',
          errorPolicy: 'ignore',
        },
        // Not sure if this affects Apollo.subscribe() functionality at all. May want to investigate if we ever want to use those.
        query: {
          fetchPolicy: 'no-cache',
          errorPolicy: 'all',
        },
        mutate: {
          fetchPolicy: 'no-cache',
          errorPolicy: 'all'
        }
      }
    };
  }

  /**
   * Get the entity names that need to be updated with the tenant (all items in the base level selectionSet of the operation)
   * @param selectionSet the selection set to scan entity names for
   * @param query the query the selection set was generated from
   * @param tenant the tenant the query is for (used to replace the old entity name with)
   * @returns the entities that need to be updated, along with their new name and if they should be replace in the result set or not
   */
  private static getGqlEntityNames(selectionSet: any[], query: string, tenant: string): GraphqlEntityReplacement[] {
    return selectionSet
      .map(selection =>
        selection?.selectionSet && selection?.name?.value
          ? {
            name: selection.name.value,
            newName: ExtendedApollo.updateEntityWithTenant(selection.name.value, tenant),
            replaceInResult: !selection.alias,
          }
          : undefined,
      )
      .filter(Boolean);
  }

  /**
   * Updates the old entity name with the tenant. If it is a mutation, then it has to have the tenant added after the mutation name
   * @param entity
   * @param tenant
   */
  private static updateEntityWithTenant(entity: string, tenant: string): string {
    if (entity.match(new RegExp(`^(${GRAPHQL_MUTATION_COMMANDS.join(`|`)})_.*`))) {
      return entity.replace(
        new RegExp(`^(${GRAPHQL_MUTATION_COMMANDS.join(`|`)})_(.*)`),
        (match, cg1, cg2) => `${cg1}_${tenant}_${cg2}`
      );
    } else {
      return `${tenant}_${entity}`;
    }
  }

  private static getGqlFragmentNames(typeConditions: any[], tenant: string): GraphqlEntityReplacement[] {
    return typeConditions
      .map(typeCondition => typeCondition.name.value)
      .map(fragmentName => ({
        name: fragmentName,
        newName: ExtendedApollo.updateEntityWithTenant(fragmentName, tenant),
        replaceInResult: true,
      }));
  }

  private static updateFragmentsWithTenant(query: string, entities: GraphqlEntityReplacement[]): string {
    return entities.reduce(
      (accumulator, currentEntity) =>
        accumulator.replace(
          // Capture groups one and three represent either the ignored tokens or punctuations that surround the entity name token, capture group two is the name token
          new RegExp(
            `(on[${GRAPHQL_IGNORED_TOKENS}]+|[${GRAPHQL_PUNCTUATION}])((?<!\{[^\{\}]+?${GRAPHQL_WHERE_OPERATORS}:\s*)${currentEntity.name}(?![^\}\{]+?\}))((_order_by)|[${GRAPHQL_IGNORED_TOKENS}]+|[${GRAPHQL_PUNCTUATION}])`,
            'g',
          ),
          (match, cg1, cg2, cg3) => {
            return `${cg1}${currentEntity.newName}${cg3}`;
          },
        ),
      query,
    );
  }

  /**
   * Update the query's entities with the tenant
   * @param query the query to update
   * @param entities the entities to replace in the query
   * @returns the updated query
   */
  private static updateQueryWithTenant(query: string, entities: GraphqlEntityReplacement[]): string {
    // ignore everything before the first query/mutation/subscription (really, there should only ever be one operation per query)
    const processedPart = query.replace(/([\s\S]*?)\b(mutation|query|subscription)\b([\s\S]*)/, (_, cg1) => cg1);
    const unprocessedPart = query.slice(processedPart.length);

    return ExtendedApollo.prependLevelOneEntitiesWithTenant(processedPart, unprocessedPart, entities);
  }

  private static prependLevelOneEntitiesWithTenant(
    processedPart: string,
    unprocessedPart: string,
    entities: GraphqlEntityReplacement[],
    level: number = 0,
    offSwitchStack: string[] = [],
  ): string {
    let nextOnSwitch: string, nextOnSwitchIndex: number = -1;
    if (offSwitchStack.length > 0) {
      nextOnSwitch = OFF_SWITCH_PAIRS[offSwitchStack[offSwitchStack.length - 1]];
      nextOnSwitchIndex = unprocessedPart.indexOf(nextOnSwitch);
    }

    const nextParenthesisIndex = unprocessedPart.indexOf('(');
    const nextQuotationMarkIndex = unprocessedPart.indexOf('"');
    const nextOpenCurlyBraceIndex = unprocessedPart.indexOf('{');
    const nextCloseCurlyBraceIndex = unprocessedPart.indexOf('}');

    const nextDelimiterIndex = Math.min(
      ...[
        nextOnSwitchIndex,
        nextParenthesisIndex,
        nextQuotationMarkIndex,
        nextOpenCurlyBraceIndex,
        nextCloseCurlyBraceIndex,
      ].filter(index => index !== -1),
    );

    let currentLevelContent = unprocessedPart.slice(0, nextDelimiterIndex + 1);

    if (level === 1 && offSwitchStack.length === 0) {
      currentLevelContent = entities.reduce(
        (acc, { name, newName }) => acc.replace(new RegExp(`\\b${name}\\b(?!.*:)`), newName),
        currentLevelContent,
      );
    }

    processedPart = processedPart + currentLevelContent;
    unprocessedPart = unprocessedPart.slice(nextDelimiterIndex + 1);

    if (nextDelimiterIndex === nextOnSwitchIndex) {
      offSwitchStack = offSwitchStack.slice(0, offSwitchStack.length - 1);
    } else if (nextDelimiterIndex === nextParenthesisIndex) {
      offSwitchStack = offSwitchStack.concat('(');
    } else if (nextDelimiterIndex === nextQuotationMarkIndex) {
      offSwitchStack = offSwitchStack.concat('"');
    } else if (nextDelimiterIndex === nextOpenCurlyBraceIndex) {
      level += 1;
    } else if (nextDelimiterIndex === nextCloseCurlyBraceIndex) {
      level -= 1;

      if (level === 0) {
        const unprocessedPartHasOperation = unprocessedPart.match(new RegExp(`\\b(${GRAPHQL_OPERATIONS.join('|')})\\b`));
        if (!unprocessedPartHasOperation) {
          return processedPart + unprocessedPart;
        }
      }
    }

    return ExtendedApollo.prependLevelOneEntitiesWithTenant(processedPart, unprocessedPart, entities, level, offSwitchStack);
  }
}
